import React, { useState, useEffect } from 'react';
import { Palette, Plus, Search, Trash2, Edit2, Star, Sparkles, AlertTriangle, X } from 'lucide-react';
import api from '../../../services/api';
import toast from 'react-hot-toast';
import Modal from '../../../components/Modal';
import { LoadingState } from '../../../components/LoadingSpinner';
import { usePageTitle } from '../../../hooks/usePageTitle';
import AIThemeModal from './AIThemeModal';

interface Theme {
  id: string;
  company_id: string;
  name: string;
  is_default: boolean;
  background_color: string;
  text_color: string;
  logo_url?: string;
  font_family: string;
  settings?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

interface ThemeFormData {
  name: string;
  is_default: boolean;
  background_color: string;
  text_color: string;
  logo_url: string;
  font_family: string;
}

const fontOptions = [
  { value: 'Inter', label: 'Inter (Padrão)' },
  { value: 'Arial', label: 'Arial' },
  { value: 'Helvetica', label: 'Helvetica' },
  { value: 'Roboto', label: 'Roboto' },
  { value: 'Open Sans', label: 'Open Sans' },
  { value: 'Lato', label: 'Lato' },
  { value: 'Montserrat', label: 'Montserrat' },
  { value: 'Poppins', label: 'Poppins' },
  { value: 'Raleway', label: 'Raleway' },
];

const inputBase =
  'block w-full bg-white border rounded-lg px-3 py-2.5 text-sm text-tinta placeholder:text-tinta-soft/70 placeholder:italic focus:outline-none focus:ring-2 transition-colors';
const inputOk = 'border-tinta/15 focus:ring-bordo/40 focus:border-bordo';
const inputErr = 'border-bordo/60 focus:ring-bordo/40 focus:border-bordo';

const ThemesPage: React.FC = () => {
  usePageTitle('Temas');
  const [themes, setThemes] = useState<Theme[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingTheme, setEditingTheme] = useState<Theme | null>(null);
  const [deletingTheme, setDeletingTheme] = useState<Theme | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [showAIModal, setShowAIModal] = useState(false);

  const [formData, setFormData] = useState<ThemeFormData>({
    name: '',
    is_default: false,
    background_color: '#ffffff',
    text_color: '#000000',
    logo_url: '',
    font_family: 'Inter',
  });

  const [formErrors, setFormErrors] = useState<Partial<Record<keyof ThemeFormData, string>>>({});

  useEffect(() => {
    loadThemes();
  }, []);

  const loadThemes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/themes');
      setThemes(response.data.themes || []);
    } catch (error: any) {
      toast.error('Erro ao carregar temas');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filteredThemes = themes.filter(theme =>
    theme.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const validateForm = () => {
    const errors: Partial<Record<keyof ThemeFormData, string>> = {};

    if (!formData.name.trim()) {
      errors.name = 'Nome é obrigatório';
    }

    const hexColorRegex = /^#[0-9A-Fa-f]{6}$/;
    if (!hexColorRegex.test(formData.background_color)) {
      errors.background_color = 'Cor de fundo inválida (use formato #RRGGBB)';
    }

    if (!hexColorRegex.test(formData.text_color)) {
      errors.text_color = 'Cor do texto inválida (use formato #RRGGBB)';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    try {
      setIsSubmitting(true);

      if (editingTheme) {
        await api.put(`/themes/${editingTheme.id}`, formData);
        const themeId = editingTheme.id;

        if (selectedFile) {
          const fd = new FormData();
          fd.append('image', selectedFile);
          await api.post(`/themes/upload/${themeId}`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }

        toast.success('Tema atualizado com sucesso!');
      } else {
        const res = await api.post('/themes', formData);
        const themeId = res.data?.id;

        if (selectedFile && themeId) {
          const fd = new FormData();
          fd.append('image', selectedFile);
          await api.post(`/themes/upload/${themeId}`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }

        toast.success('Tema criado com sucesso!');
      }

      await loadThemes();
      closeModal();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao salvar tema');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingTheme) return;

    try {
      setIsSubmitting(true);
      await api.delete(`/themes/${deletingTheme.id}`);
      toast.success('Tema excluído com sucesso!');
      await loadThemes();
      setIsDeleteModalOpen(false);
      setDeletingTheme(null);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao excluir tema');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCreateModal = () => {
    setEditingTheme(null);
    setFormData({
      name: '',
      is_default: false,
      background_color: '#ffffff',
      text_color: '#000000',
      logo_url: '',
      font_family: 'Inter',
    });
    setFormErrors({});
    setSelectedFile(null);
    setImagePreview('');
    setIsModalOpen(true);
  };

  const openEditModal = (theme: Theme) => {
    setEditingTheme(theme);
    setFormData({
      name: theme.name,
      is_default: theme.is_default,
      background_color: theme.background_color,
      text_color: theme.text_color,
      logo_url: theme.logo_url || '',
      font_family: theme.font_family,
    });
    setFormErrors({});
    setSelectedFile(null);
    setImagePreview(theme.logo_url || '');
    setIsModalOpen(true);
  };

  const openDeleteModal = (theme: Theme) => {
    setDeletingTheme(theme);
    setIsDeleteModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingTheme(null);
    setFormData({
      name: '',
      is_default: false,
      background_color: '#ffffff',
      text_color: '#000000',
      logo_url: '',
      font_family: 'Inter',
    });
    setFormErrors({});
    setSelectedFile(null);
    setImagePreview('');
  };

  const handleInputChange = (field: keyof ThemeFormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione uma imagem válida');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande. Tamanho máximo: 5MB');
      return;
    }

    setSelectedFile(file);

    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setSelectedFile(null);
    setImagePreview('');
    setFormData(prev => ({ ...prev, logo_url: '' }));
  };

  if (loading) {
    return <LoadingState message="Carregando temas..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-black text-tinta leading-tight">
            Seus <span className="italic text-bordo">temas</span>
          </h1>
          <p className="mt-2 text-sm text-tinta-soft">
            Gerencie a aparência das suas rodas de prêmios com temas personalizados
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-2">
          <button
            onClick={() => setShowAIModal(true)}
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-bold text-tinta bg-mostarda hover:bg-mostarda-light border border-mostarda-dark/30 transition-colors"
          >
            <Sparkles className="mr-2 h-4 w-4" />
            Gerar com IA
          </button>
          <button
            onClick={openCreateModal}
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-bold text-creme bg-bordo hover:bg-bordo-dark transition-colors"
          >
            <Plus className="mr-2 h-4 w-4" />
            Novo Tema
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-tinta-soft" />
        </div>
        <input
          type="text"
          className="block w-full pl-10 pr-3 py-2.5 bg-white border border-tinta/15 rounded-lg text-sm text-tinta placeholder:text-tinta-soft/70 placeholder:italic focus:outline-none focus:ring-2 focus:ring-bordo/40 focus:border-bordo transition-colors"
          placeholder="Buscar temas..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Themes List */}
      {filteredThemes.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-tinta/10 shadow-sm">
          <Palette className="mx-auto h-12 w-12 text-tinta-soft/60" strokeWidth={1.5} />
          <h3 className="mt-3 font-display text-base font-bold text-tinta">Nenhum tema encontrado</h3>
          <p className="mt-1 text-sm text-tinta-soft italic">
            Comece criando um novo tema para personalizar suas rodas.
          </p>
          <div className="mt-6">
            <button
              onClick={openCreateModal}
              className="inline-flex items-center px-4 py-2.5 rounded-lg text-sm font-bold text-creme bg-bordo hover:bg-bordo-dark transition-colors"
            >
              <Plus className="-ml-1 mr-2 h-5 w-5" />
              Novo Tema
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredThemes.map((theme) => (
            <ThemeCard
              key={theme.id}
              theme={theme}
              onEdit={openEditModal}
              onDelete={openDeleteModal}
            />
          ))}
        </div>
      )}

      {/* Theme Form Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingTheme ? 'Editar Tema' : 'Novo Tema'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Nome do Tema */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-tinta mb-1.5">
              Nome do Tema *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`${inputBase} ${formErrors.name ? inputErr : inputOk}`}
              placeholder="Ex: Tema Natal, Tema Escuro"
            />
            {formErrors.name && (
              <p className="mt-1 text-sm text-bordo italic">{formErrors.name}</p>
            )}
          </div>

          {/* Tema Padrão */}
          <div>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                id="is_default"
                checked={formData.is_default}
                onChange={(e) => handleInputChange('is_default', e.target.checked)}
                className="h-4 w-4 rounded border-tinta/30 text-bordo focus:ring-bordo/40 focus:ring-offset-0 cursor-pointer"
              />
              <span className="ml-2 text-sm font-bold text-tinta">Definir como tema padrão</span>
            </label>
          </div>

          {/* Cores */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-tinta mb-1.5">
                Cor de Fundo *
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={formData.background_color}
                  onChange={(e) => handleInputChange('background_color', e.target.value)}
                  className="h-10 w-14 flex-shrink-0 border border-tinta/15 rounded-lg cursor-pointer bg-white"
                />
                <input
                  type="text"
                  value={formData.background_color}
                  onChange={(e) => handleInputChange('background_color', e.target.value)}
                  className={`flex-1 ${inputBase} ${formErrors.background_color ? inputErr : inputOk}`}
                  placeholder="#FFFFFF"
                />
              </div>
              {formErrors.background_color && (
                <p className="mt-1 text-sm text-bordo italic">{formErrors.background_color}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-tinta mb-1.5">
                Cor do Texto *
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={formData.text_color}
                  onChange={(e) => handleInputChange('text_color', e.target.value)}
                  className="h-10 w-14 flex-shrink-0 border border-tinta/15 rounded-lg cursor-pointer bg-white"
                />
                <input
                  type="text"
                  value={formData.text_color}
                  onChange={(e) => handleInputChange('text_color', e.target.value)}
                  className={`flex-1 ${inputBase} ${formErrors.text_color ? inputErr : inputOk}`}
                  placeholder="#000000"
                />
              </div>
              {formErrors.text_color && (
                <p className="mt-1 text-sm text-bordo italic">{formErrors.text_color}</p>
              )}
            </div>
          </div>

          {/* Fonte */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-tinta mb-1.5">
              Fonte *
            </label>
            <select
              value={formData.font_family}
              onChange={(e) => handleInputChange('font_family', e.target.value)}
              className={`${inputBase} ${inputOk}`}
            >
              {fontOptions.map((font) => (
                <option key={font.value} value={font.value}>
                  {font.label}
                </option>
              ))}
            </select>
          </div>

          {/* Logotipo */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-tinta mb-2">
              Logotipo
            </label>

            <div className="mb-3">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="block w-full text-sm text-tinta-soft file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-bold file:bg-bordo/10 file:text-bordo file:cursor-pointer hover:file:bg-bordo/15 transition-colors"
              />
              <p className="mt-1 text-xs text-tinta-soft italic">
                Faça upload do logotipo (jpg, png, webp). Máx 5MB.
              </p>
            </div>

            <div>
              <input
                type="text"
                value={formData.logo_url}
                onChange={(e) => handleInputChange('logo_url', e.target.value)}
                className={`${inputBase} ${inputOk}`}
                placeholder="Ou cole a URL: https://exemplo.com/logo.png"
              />
            </div>

            {imagePreview && (
              <div className="mt-3 flex items-center gap-3 p-3 bg-creme-light border border-tinta/10 rounded-lg">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="h-16 w-16 object-contain border border-tinta/15 rounded bg-white"
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="text-sm font-bold text-bordo hover:text-bordo-dark inline-flex items-center gap-1.5"
                >
                  <X className="h-4 w-4" />
                  Remover
                </button>
              </div>
            )}
          </div>

          {/* Preview do Tema — keeps user's colors verbatim */}
          <div className="pt-4 border-t border-tinta/10">
            <label className="block text-xs font-bold uppercase tracking-wider text-tinta mb-2">
              Prévia do Tema
            </label>
            <div
              className="rounded-lg p-6 text-center border border-tinta/15"
              style={{
                backgroundColor: formData.background_color,
                color: formData.text_color,
                fontFamily: formData.font_family,
              }}
            >
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Logo"
                  className="h-12 mx-auto mb-3 object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              )}
              <p className="text-xl font-semibold mb-2">Roda de Prêmios</p>
              <p className="text-sm">Este é um exemplo de como seu tema ficará</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-tinta/10">
            <button
              type="button"
              onClick={closeModal}
              className="w-full sm:w-auto px-4 py-2 border border-tinta/15 rounded-lg text-sm font-semibold text-tinta-soft hover:bg-creme-light hover:text-tinta focus:outline-none focus:ring-2 focus:ring-bordo/30 transition-colors"
              disabled={isSubmitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto px-5 py-2 rounded-lg text-sm font-bold text-creme bg-bordo hover:bg-bordo-dark focus:outline-none focus:ring-2 focus:ring-bordo/40 disabled:opacity-50 transition-colors"
            >
              {isSubmitting ? 'Salvando...' : (editingTheme ? 'Salvar' : 'Criar')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirmar Exclusão"
        size="sm"
      >
        {deletingTheme && (
          <div className="space-y-4">
            <div className="flex gap-3 p-4 rounded-lg bg-bordo/8 border border-bordo/30">
              <AlertTriangle className="w-5 h-5 text-bordo mt-0.5 flex-shrink-0" />
              <p className="text-sm text-tinta leading-relaxed">
                Tem certeza que deseja excluir o tema <strong className="font-display italic">"{deletingTheme.name}"</strong>? As rodas que usam este tema passarão a usar o tema padrão.
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-2">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-4 py-2 border border-tinta/15 rounded-lg text-sm font-semibold text-tinta-soft hover:bg-creme-light hover:text-tinta focus:outline-none focus:ring-2 focus:ring-bordo/30 transition-colors"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={isSubmitting}
                className="px-4 py-2 rounded-lg text-sm font-bold text-creme bg-bordo hover:bg-bordo-dark focus:outline-none focus:ring-2 focus:ring-bordo/40 disabled:opacity-50 transition-colors"
              >
                {isSubmitting ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* AI Theme Modal */}
      <AIThemeModal
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        onSelect={(theme) => {
          setFormData(prev => ({
            ...prev,
            name: theme.name,
            background_color: theme.background_color,
            text_color: theme.text_color,
            font_family: theme.font_family,
          }));
          setEditingTheme(null);
          setFormErrors({});
          setSelectedFile(null);
          setImagePreview('');
          setIsModalOpen(true);
        }}
      />
    </div>
  );
};

interface ThemeCardProps {
  theme: Theme;
  onEdit: (theme: Theme) => void;
  onDelete: (theme: Theme) => void;
}

const ThemeCard: React.FC<ThemeCardProps> = ({ theme, onEdit, onDelete }) => {
  return (
    <div className="bg-white border border-tinta/10 rounded-xl overflow-hidden hover:shadow-md hover:border-tinta/20 transition-all">
      {/* Preview — keeps user's theme colors verbatim */}
      <div
        className="h-32 p-4 flex items-center justify-center border-b border-tinta/10"
        style={{
          backgroundColor: theme.background_color,
          color: theme.text_color,
          fontFamily: theme.font_family,
        }}
      >
        {theme.logo_url ? (
          <img
            src={theme.logo_url}
            alt={theme.name}
            className="max-h-16 object-contain"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <p className="text-lg font-semibold">Prévia do Tema</p>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2 gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-bold text-tinta truncate leading-tight">
              {theme.name}
            </h3>
            {theme.is_default && (
              <span className="inline-flex items-center mt-1.5 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-mostarda/20 text-mostarda-dark border border-mostarda/40">
                <Star className="h-3 w-3 mr-1" />
                Padrão
              </span>
            )}
          </div>
        </div>

        <div className="space-y-1.5 text-xs text-tinta-soft mb-4">
          <div className="flex items-center">
            <span className="font-semibold mr-2 text-tinta">Fundo:</span>
            <span className="inline-flex items-center">
              <span
                className="w-4 h-4 rounded border border-tinta/15 mr-1.5"
                style={{ backgroundColor: theme.background_color }}
              />
              <span className="font-mono">{theme.background_color}</span>
            </span>
          </div>
          <div className="flex items-center">
            <span className="font-semibold mr-2 text-tinta">Texto:</span>
            <span className="inline-flex items-center">
              <span
                className="w-4 h-4 rounded border border-tinta/15 mr-1.5"
                style={{ backgroundColor: theme.text_color }}
              />
              <span className="font-mono">{theme.text_color}</span>
            </span>
          </div>
          <div>
            <span className="font-semibold text-tinta">Fonte:</span> <span className="italic">{theme.font_family}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-3 border-t border-tinta/10">
          <button
            onClick={() => onEdit(theme)}
            className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-bold text-bordo bg-bordo/8 border border-bordo/20 rounded-lg hover:bg-bordo/15 transition-colors"
          >
            <Edit2 className="h-3.5 w-3.5" />
            Editar
          </button>
          <button
            onClick={() => onDelete(theme)}
            className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-semibold text-tinta-soft bg-white border border-tinta/15 rounded-lg hover:bg-bordo/8 hover:text-bordo hover:border-bordo/30 transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Excluir
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThemesPage;
